import React from "react";
import { Outlet, useParams } from "react-router-dom";
import { Stack, Tooltip, Typography } from "@mui/material";
import useAsyncRequest from "hooks/useAsyncRequest";
import { showTitleWhenClipped } from "utils/showTitleWhenClipped";
import { LoadablePage } from "shared/LoadablePage";
import CopyToClipboardWrapper from "components/CopyToClipboardWrapper";
import { EntityProvider } from "components/EntityProvider/EntityProvider";
import NavigationSidebarLayout, { Navigation } from "components/NavigationSidebarLayout/NavigationSidebarLayout";
import { RbacPolicies } from "components/authorization/RbacPolicies";
import { ReturnToOrderButton } from "areas/orders/components/ReturnToOrderButton";
import { useReturnToOrder } from "areas/orders/hooks/useReturnToOrder";
import { withReturnToOrder } from "areas/orders/order-routes";
import { billingAccountRoutes } from "../billing-account-routes";
import { getBillingAccountByCode } from "../services/billingAccountService";

function getValueOrFallbackFor(billingAccountCode?: string) {
  return billingAccountCode || "UNKNOWN_BILLING_ACCOUNT";
}

export function BillingAccountDetailLayout() {
  const { billingAccountCode } = useParams<{ billingAccountCode: string }>();
  const returnToOrderOdNumber = useReturnToOrder(billingAccountCode);
  const withReturn = (path: string) => withReturnToOrder(path, returnToOrderOdNumber);

  const {
    data: billingAccount,
    loading,
    error,
    refresh,
  } = useAsyncRequest(getBillingAccountByCode, getValueOrFallbackFor(billingAccountCode));

  const navLinks = [
    Navigation.navItem(
      "Details",
      withReturn(billingAccountRoutes.detail(billingAccountCode ?? "").root),
      true,
      RbacPolicies.BillingAccounts.Read
    ),
    Navigation.navItem(
      "Subscriptions",
      withReturn(billingAccountRoutes.detail(billingAccountCode ?? "").subscriptions),
      false,
      RbacPolicies.BillingAccounts.Read
    ),
    Navigation.navItem(
      "History",
      withReturn(billingAccountRoutes.detail(billingAccountCode ?? "").history),
      false,
      RbacPolicies.BillingAccounts.Read
    ),
  ];

  const legalName = billingAccount?.legalName ?? "";

  const preNavbarComponent = (
    <Stack spacing={0.5}>
      <Typography variant="body1" noWrap onMouseEnter={showTitleWhenClipped(legalName)}>
        {legalName}
      </Typography>
      <CopyToClipboardWrapper value={billingAccountCode}>
        <Tooltip title="Billing account code">
          <span>{billingAccountCode}</span>
        </Tooltip>
      </CopyToClipboardWrapper>
    </Stack>
  );

  return (
    <LoadablePage loading={loading && !billingAccount} error={error}>
      <EntityProvider entity={billingAccount} refresh={refresh}>
        <ReturnToOrderButton odNumber={returnToOrderOdNumber} />
        <NavigationSidebarLayout navLinks={navLinks} name={"Billing account"} preNavbarComponent={preNavbarComponent}>
          <Outlet />
        </NavigationSidebarLayout>
      </EntityProvider>
    </LoadablePage>
  );
}
